import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LoadingIndicator, SearchBar, Typography } from 'lib/components';
import { InvoicePaymentStatusType, InvoiceStatusType } from 'lib/util';
import { useAddIdPrefix, useComprehensiveTopNav, useWindowResize } from 'hooks';
import {
	actions,
	useAppDispatch,
	useSelectAuthorization,
	useSelectHospital,
	useSelectHospitals,
	useSelectInvoices,
	useSelectSettings,
} from 'store';
import { Invoice } from 'types';
import adjustTime from '../../lib/adjustTime';
import {
	BillingReviewBucket,
	BillingReviewStatus,
	billingReviewStatusLabel,
	countByBucket,
	getBillingReviewStatus,
	invoiceMatchesBucket,
} from './reviewStatus';
import classes from './index.module.scss';

const REVIEW_BADGE_CLASS: Record<BillingReviewStatus, string> = {
	pending: classes['billing-badge-pending'],
	needs_fix: classes['billing-badge-needs_fix'],
	approved: classes['billing-badge-approved'],
	sent: classes['billing-badge-sent'],
	voided: classes['billing-badge-voided'],
};

const BUCKETS: { id: BillingReviewBucket; label: string }[] = [
	{ id: 'needs_review', label: 'Needs review' },
	{ id: 'needs_fix', label: 'Needs fix' },
	{ id: 'approved', label: 'Approved' },
	{ id: 'sent', label: 'Sent' },
	{ id: 'all', label: 'All' },
];

const invoiceFlags = (invoice: Invoice): string[] => {
	const flags: string[] = [];
	if (invoice.jobId == null && invoice.rangeFrom) flags.push('Accrued');
	if (invoice.total === 0) flags.push('$0');
	if (invoice.status === InvoiceStatusType.VOIDED || invoice.reason) flags.push('Voided');
	if (invoice.paymentStatus === InvoicePaymentStatusType.UNPAID && !invoice.path) flags.push('No PDF');
	return flags;
};

/**
 * Billing review queue — test panel for pre-send invoice review.
 * Review status is client-derived (+ localStorage overrides) until backend lands.
 */
const BillingScreen = () => {
	const { push } = useHistory();
	const { isSmallerDevice } = useWindowResize();
	const addPrefix = useAddIdPrefix();
	const dispatch = useAppDispatch();
	const invoices = useSelectInvoices();
	const hospitals = useSelectHospitals();
	const settingsState = useSelectSettings();
	const getHospitalById = useSelectHospital();
	const { canReadInvoice } = useSelectAuthorization();

	const [bucket, setBucket] = useState<BillingReviewBucket>('needs_review');
	const [query, setQuery] = useState('');
	const [ready, setReady] = useState(false);

	useComprehensiveTopNav('Billing');

	useEffect(() => {
		dispatch(actions.fetchInvoices());
		dispatch(actions.fetchHospitals());
	}, [dispatch]);

	useEffect(() => {
		if (hospitals.list.length > 0 || invoices.updatedAt) {
			setReady(true);
		}
	}, [hospitals.list.length, invoices.updatedAt]);

	const counts = useMemo(() => countByBucket(invoices.list), [invoices.list, invoices.updatedAt]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		return invoices.list
			.filter((invoice) => invoiceMatchesBucket(invoice, bucket))
			.filter((invoice) => {
				if (!q) return true;
				const hospitalName = getHospitalById(invoice.hospitalId)?.name ?? '';
				const idStr = String(invoice.id);
				const jobStr = invoice.jobId != null ? String(invoice.jobId) : '';
				return (
					hospitalName.toLowerCase().includes(q) ||
					idStr.includes(q) ||
					jobStr.includes(q) ||
					addPrefix(invoice.id).toLowerCase().includes(q)
				);
			})
			.sort((a, b) => b.id - a.id);
	}, [invoices.list, invoices.updatedAt, bucket, query, getHospitalById, addPrefix]);

	const openReview = (invoice: Invoice) => {
		if (!canReadInvoice) return;
		push(`/billing/review/${invoice.id}`);
	};

	if (!ready) return <LoadingIndicator />;

	return (
		<div className={classes['billing']}>
			<div className={classes['billing-banner']}>
				<Typography variant={isSmallerDevice ? 'h6' : 'h5'} weight="medium">
					Invoice review (test panel)
				</Typography>
				<Typography variant="h6" style={{ marginTop: '0.35rem', opacity: 0.75 }}>
					Pre-send queue. Approve / Needs fix are stored locally until review APIs exist. Existing Invoices
					nav is unchanged.
				</Typography>
			</div>

			<div className={classes['billing-tabs']}>
				{BUCKETS.map(({ id, label }) => (
					<button
						key={id}
						type="button"
						className={`${classes['billing-tab']} ${
							bucket === id ? classes['billing-tab-active'] : ''
						}`}
						onClick={() => setBucket(id)}
					>
						<Typography variant="h6">{label}</Typography>
						<span className={classes['billing-tabCount']}>{counts[id]}</span>
					</button>
				))}
			</div>

			<div className={classes['billing-toolbar']}>
				<SearchBar
					query={query}
					setQuery={setQuery}
					className={classes['billing-search']}
					placeholder="Search hospital, invoice, or job ID…"
				/>
			</div>

			<div className={classes['billing-tableWrap']}>
				{filtered.length === 0 ? (
					<div className={classes['billing-empty']}>
						<Typography variant="h5">No invoices in this bucket</Typography>
					</div>
				) : (
					<table className={classes['billing-table']}>
						<thead>
							<tr>
								<th>ID</th>
								<th>Review</th>
								<th>Hospital</th>
								<th>Job / Range</th>
								<th>Total</th>
								<th>Created</th>
								<th>Flags</th>
							</tr>
						</thead>
						<tbody>
							{filtered.map((invoice) => {
								const status = getBillingReviewStatus(invoice);
								const hospitalName =
									invoice.hospital?.name ?? getHospitalById(invoice.hospitalId)?.name ?? '—';
								const jobOrRange =
									invoice.jobId != null
										? addPrefix(invoice.jobId)
										: invoice.rangeFrom && invoice.rangeTo
										? `${invoice.rangeFrom} – ${invoice.rangeTo}`
										: '—';
								const flags = invoiceFlags(invoice);

								return (
									<tr key={invoice.id} onClick={() => openReview(invoice)}>
										<td>{addPrefix(invoice.id)}</td>
										<td>
											<span className={`${classes['billing-badge']} ${REVIEW_BADGE_CLASS[status]}`}>
												{billingReviewStatusLabel(status)}
											</span>
										</td>
										<td>{hospitalName}</td>
										<td>{jobOrRange}</td>
										<td>${invoice.total.toFixed(2)}</td>
										<td>
											{adjustTime(settingsState, invoice.createdAt).format('MM/DD/YYYY hh:mm A')}
										</td>
										<td>
											{flags.map((flag) => (
												<span
													key={flag}
													className={`${classes['billing-badge']} ${classes['billing-badge-warn']}`}
												>
													{flag}
												</span>
											))}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
};

export default BillingScreen;
